<template>
  <Teleport to="body">
    <Transition name="modal-fade">
      <div v-if="isOpen" 
           class="fixed inset-0 z-[2000] flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-sm overflow-y-auto"
           :style="{ paddingTop: 'max(0.75rem, env(safe-area-inset-top, 0px))', paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom, 0px))' }"
           @click.self="close">
        <div class="relative w-full max-w-5xl bg-white dark:bg-slate-900 rounded-xl shadow-2xl border border-slate-200/80 dark:border-slate-800 text-slate-900 dark:text-slate-100 transition-all p-5 sm:p-7 md:p-8 my-auto max-h-[96vh] overflow-y-auto lg:overflow-visible no-scrollbar">
          
          <!-- Close Button -->
          <button @click="close" 
                  type="button" 
                  class="absolute top-3.5 right-3.5 sm:top-4 sm:right-4 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 bg-slate-100/80 dark:bg-slate-800/80 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg p-1.5 transition-colors cursor-pointer z-20"
                  aria-label="Close">
            <XMarkIcon class="w-5 h-5" />
            <span class="sr-only">Close</span>
          </button>

          <!-- Header -->
          <div class="space-y-2 text-center max-w-2xl mx-auto pt-7 sm:pt-2">
            
            <!-- Unified Eco Banner Badge -->
            <div class="inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-semibold bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border border-indigo-500/20 max-w-[calc(100%-4rem)] mx-auto">
              <SparklesIcon class="w-3.5 h-3.5 text-indigo-500 shrink-0" />
              <span>{{ $t('subscriptionPage.unifiedBadge') || 'Uma Assinatura • Acesso Total ao Live & Studio' }}</span>
            </div>

            <h2 class="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white">
              {{ $t('subscriptionPage.upgradeTitle') || 'Evolua sua Música com o Triad Pro' }}
            </h2>
            <p class="text-xs sm:text-sm text-slate-500 dark:text-slate-400 max-w-lg mx-auto leading-relaxed">
              {{ $t('subscriptionPage.upgradeDesc') || 'Uma única assinatura para palco no Live e produção com IA no Studio.' }}
            </p>

            <!-- Billing Toggle: Segmented Control -->
            <div class="pt-2">
              <div class="inline-flex items-center p-1 rounded-lg bg-slate-100 dark:bg-slate-800 border border-slate-200/80 dark:border-slate-700/80">
                <button 
                  type="button"
                  @click="billingCycle = 'monthly'"
                  class="px-4 py-1.5 rounded-md text-xs font-bold transition-all cursor-pointer"
                  :class="billingCycle === 'monthly' ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-xs' : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'">
                  {{ $t('subscriptionPage.monthly') || 'Mensal' }}
                </button>

                <button 
                  type="button"
                  @click="billingCycle = 'yearly'"
                  class="px-4 py-1.5 rounded-md text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5"
                  :class="billingCycle === 'yearly' ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-xs' : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'">
                  <span>{{ $t('subscriptionPage.yearly') || 'Anual' }}</span>
                  <span class="text-[10px] font-extrabold px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                    {{ $t('subscriptionPage.saveYearly') || 'Economize 17%' }}
                  </span>
                </button>
              </div>
            </div>

            <!-- Amazon Fire Tablet Banner -->
            <div v-if="isAmazon" class="mt-2.5 p-2.5 rounded-lg bg-amber-50/90 dark:bg-amber-950/60 border border-amber-200/80 dark:border-amber-800/60 text-center max-w-xl mx-auto flex items-center justify-center gap-2">
              <span class="text-base">🛒</span>
              <p class="text-xs text-amber-900 dark:text-amber-200 font-medium">
                {{ $t('subscriptionPage.amazonNotice') }}
              </p>
            </div>

            <!-- Native App Multiplatform Notice (When In-App Purchases are temporarily unavailable and not Amazon) -->
            <div v-else-if="isNative && !hasKey" class="mt-2.5 p-2.5 rounded-lg bg-indigo-50/90 dark:bg-indigo-950/60 border border-indigo-200/80 dark:border-indigo-800/60 text-center max-w-xl mx-auto">
              <p class="text-xs text-indigo-900 dark:text-indigo-200 font-medium">
                {{ $t('subscriptionPage.nativeNoticeMultiplatform', 'As assinaturas e planos são sincronizados em todos os seus dispositivos. Suas compras ficam disponíveis automaticamente aqui no aplicativo!') }}
              </p>
            </div>

            <!-- Platform Shared Credits Notice Banner -->
            <div class="mt-3 p-2.5 rounded-lg bg-purple-50/90 dark:bg-purple-950/40 border border-purple-200/80 dark:border-purple-800/60 text-center max-w-xl mx-auto flex items-center justify-center gap-2 shadow-xs">
              <SparklesIcon class="w-4 h-4 text-purple-600 dark:text-purple-400 shrink-0" />
              <p class="text-xs text-purple-900 dark:text-purple-200 font-medium">
                {{ $t('subscriptionPage.sharedCreditsBanner') || 'Os créditos de IA pertencem à plataforma Triad e são compartilhados entre o Live e o Studio.' }}
              </p>
            </div>
          </div>

          <!-- Pricing Cards Grid -->
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5 pt-6 items-stretch">
            
            <!-- 1. Free Plan -->
            <div class="border border-slate-200 dark:border-slate-800 rounded-xl p-4 sm:p-5 flex flex-col justify-between bg-slate-50/60 dark:bg-slate-800/40 hover:border-slate-300 dark:hover:border-slate-700 transition-all text-left">
              <div class="space-y-3">
                <div class="flex items-center justify-between">
                  <div class="w-10 h-10 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 flex items-center justify-center text-slate-600 dark:text-slate-300 shadow-xs">
                    <MusicalNoteIcon class="w-5 h-5" />
                  </div>
                  <span class="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">{{ $t('subscriptionPage.basic') || 'Básico' }}</span>
                </div>

                <div>
                  <h3 class="text-lg font-bold text-slate-900 dark:text-white">
                    {{ $t('subscriptionPage.planNames.free') || $t('subscriptionPage.free') || 'Free' }}
                  </h3>
                  <p class="text-[11px] text-slate-500 dark:text-slate-400">
                    {{ $t('subscriptionPage.planTaglines.free') || 'Live + Studio Essentials' }}
                  </p>
                </div>

                <div>
                  <div class="text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">
                    {{ currency }}0<span class="text-xs font-normal text-slate-500 dark:text-slate-400">{{ perMonthText }}</span>
                  </div>
                  <div class="text-[11px] text-slate-400 dark:text-slate-500 min-h-[16px]">
                    {{ $t('subscriptionPage.free') || 'Gratuito' }}
                  </div>
                </div>

                <button type="button" 
                        disabled 
                        class="w-full py-2.5 px-3 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-400 dark:text-slate-500 text-xs font-bold bg-white/70 dark:bg-slate-800/50 cursor-default text-center">
                  {{ isSubscribed ? ($t('subscriptionPage.included') || 'Included') : ($t('subscriptionPage.currentPlan') || 'Current Plan') }}
                </button>

                <!-- Features Section -->
                <div class="space-y-3 pt-3 border-t border-slate-200/80 dark:border-slate-700/80 text-left">
                  <div>
                    <div class="text-[10px] font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400 mb-1.5 flex items-center gap-1.5">
                      <span class="w-1.5 h-1.5 rounded-full bg-indigo-500"></span>
                      <span>Triad Live</span>
                    </div>
                    <ul class="space-y-1.5 text-[11px] text-slate-600 dark:text-slate-300">
                      <li class="flex items-start gap-2">
                        <CheckIcon class="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                        <span>{{ $t('subscriptionPage.freeFeatures.livePlaylists') || '2 Playlists e 1 Equipe' }}</span>
                      </li>
                      <li class="flex items-start gap-2">
                        <CheckIcon class="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                        <span>{{ $t('subscriptionPage.freeFeatures.liveLimit') || 'Até 10 músicas por playlist' }}</span>
                      </li>
                      <li class="flex items-start gap-2">
                        <CheckIcon class="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                        <span>{{ $t('subscriptionPage.freeFeatures.liveChords') || 'Cifras e transposição básicas' }}</span>
                      </li>
                    </ul>
                  </div>

                  <div>
                    <div class="text-[10px] font-bold uppercase tracking-wider text-purple-600 dark:text-purple-400 mb-1.5 flex items-center gap-1.5">
                      <span class="w-1.5 h-1.5 rounded-full bg-purple-500"></span>
                      <span>Triad Studio</span>
                    </div>
                    <ul class="space-y-1.5 text-[11px] text-slate-600 dark:text-slate-300">
                      <li class="flex items-start gap-2">
                        <CheckIcon class="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                        <span>{{ $t('subscriptionPage.freeFeatures.createProjects') || 'Criação de Projetos e Faixas' }}</span>
                      </li>
                      <li class="flex items-start gap-2">
                        <CheckIcon class="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                        <span>{{ $t('subscriptionPage.freeFeatures.importAudio') || 'Importar Áudio e Gravar' }}</span>
                      </li>
                    </ul>
                  </div>

                  <div>
                    <div class="text-[10px] font-bold uppercase tracking-wider text-amber-600 dark:text-amber-400 mb-1.5 flex items-center gap-1.5">
                      <SparklesIcon class="w-3 h-3 text-amber-500" />
                      <span>{{ $t('subscriptionPage.platformCreditsTitle') || 'Créditos de IA (Plataforma)' }}</span>
                    </div>
                    <ul class="space-y-1.5 text-[11px] text-slate-600 dark:text-slate-300">
                      <li class="flex items-start gap-2">
                        <CheckIcon class="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                        <span class="font-semibold">{{ $t('subscriptionPage.freeFeatures.welcomeCredits') || '2 Créditos Grátis de IA (Live & Studio)' }}</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <!-- 2. Musician Tier (Popular / Most Chosen) -->
            <div class="relative rounded-xl border-2 border-indigo-600 dark:border-indigo-500 bg-white dark:bg-slate-900 p-4 sm:p-5 flex flex-col justify-between shadow-xl shadow-indigo-500/10 dark:shadow-indigo-950/40 text-left">
              <!-- Floating Popular Badge -->
              <div class="absolute -top-3 left-1/2 -translate-x-1/2 px-3.5 py-0.5 rounded-md bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-[10px] font-black tracking-wider uppercase shadow-sm whitespace-nowrap">
                {{ $t('subscriptionPage.mostPopular') || 'Mais Escolhido' }}
              </div>

              <div class="space-y-3">
                <div class="flex items-center justify-between">
                  <div class="w-10 h-10 rounded-lg bg-indigo-50 dark:bg-indigo-950/80 border border-indigo-200/80 dark:border-indigo-800/80 flex items-center justify-center text-indigo-600 dark:text-indigo-400 shadow-xs">
                    <SparklesIcon class="w-5 h-5" />
                  </div>
                </div>

                <div>
                  <h3 class="text-lg font-bold text-indigo-600 dark:text-indigo-400">
                    {{ $t('subscriptionPage.planNames.musician') || 'The Musician' }}
                  </h3>
                  <p class="text-[11px] text-slate-500 dark:text-slate-400">
                    {{ $t('subscriptionPage.planTaglines.musician') || 'Live Total + Studio IA' }}
                  </p>
                </div>

                <div>
                  <div class="text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">
                    {{ displayMusicianPrice }}
                    <span class="text-xs font-normal text-slate-500 dark:text-slate-400">{{ perMonthText }}</span>
                  </div>
                  <div class="text-[11px] min-h-[16px]">
                    <span v-if="billingCycle === 'yearly'" class="text-emerald-600 dark:text-emerald-400 font-semibold">
                      {{ displayMusicianYearlyTotal }} {{ billedAnnuallyText }}
                    </span>
                    <span v-else class="text-slate-400 dark:text-slate-500">
                      {{ billedMonthlyText }}
                    </span>
                  </div>
                </div>

                <button v-if="isMusician"
                        type="button" 
                        disabled 
                        class="w-full py-2.5 px-3 rounded-lg border border-indigo-200 dark:border-indigo-800 text-indigo-600 dark:text-indigo-400 text-xs font-bold bg-indigo-50/60 dark:bg-indigo-950/40 cursor-default text-center">
                  {{ $t('subscriptionPage.currentPlan') || 'Current Plan' }}
                </button>
                <button v-else-if="isBackstage || isAdmin"
                        type="button" 
                        disabled 
                        class="w-full py-2.5 px-3 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-400 dark:text-slate-500 text-xs font-bold bg-transparent cursor-default text-center">
                  {{ $t('subscriptionPage.included') || 'Included' }}
                </button>
                <button v-else-if="canSubscribe"
                        type="button" 
                        @click="handleSubscribe(billingCycle === 'monthly' ? STRIPE_PRICE_ID_MUSICIAN_MONTHLY : STRIPE_PRICE_ID_MUSICIAN_YEARLY)"
                        :disabled="isLoading"
                        class="w-full py-2.5 px-3 rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 active:scale-95 text-white text-xs font-bold shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed">
                  <svg v-if="loadingPriceId === (billingCycle === 'monthly' ? STRIPE_PRICE_ID_MUSICIAN_MONTHLY : STRIPE_PRICE_ID_MUSICIAN_YEARLY)" 
                       class="animate-spin h-3.5 w-3.5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
                  </svg>
                  <span>{{ isAmazon ? ($t('subscriptionPage.subscribeOnWeb') || 'Assinar no Site') : ($t('subscriptionPage.subscribeBtn') || 'Subscribe') }}</span>
                </button>
                <div v-else class="w-full py-2.5 px-3 rounded-lg bg-slate-100 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 text-xs font-medium text-center">
                  {{ isNative ? ($t('subscriptionPage.temporarilyUnavailable') || 'Indisponível no momento') : ($t('subscriptionPage.availableOnWeb') || 'Disponível no site') }}
                </div>

                <!-- Features Section -->
                <div class="space-y-3 pt-3 border-t border-indigo-100 dark:border-indigo-900/50 text-left">
                  <div>
                    <div class="text-[10px] font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400 mb-1.5 flex items-center gap-1.5">
                      <span class="w-1.5 h-1.5 rounded-full bg-indigo-500"></span>
                      <span>Triad Live</span>
                    </div>
                    <ul class="space-y-1.5 text-[11px] text-slate-700 dark:text-slate-200">
                      <li class="flex items-start gap-2">
                        <CheckIcon class="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                        <span class="font-semibold">{{ $t('subscriptionPage.musicianFeatures.liveSongsLimit') || 'Até 20 músicas por playlist' }}</span>
                      </li>
                      <li class="flex items-start gap-2">
                        <CheckIcon class="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                        <span>{{ $t('subscriptionPage.musicianFeatures.livePlaylistsLimit') || 'Até 15 playlists e 3 equipes' }}</span>
                      </li>
                      <li class="flex items-start gap-2">
                        <CheckIcon class="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                        <span>{{ $t('subscriptionPage.musicianFeatures.livePdfOffline') || 'Exportação PDF, anotações e offline' }}</span>
                      </li>
                    </ul>
                  </div>

                  <div>
                    <div class="text-[10px] font-bold uppercase tracking-wider text-purple-600 dark:text-purple-400 mb-1.5 flex items-center gap-1.5">
                      <span class="w-1.5 h-1.5 rounded-full bg-purple-500"></span>
                      <span>Triad Studio</span>
                    </div>
                    <ul class="space-y-1.5 text-[11px] text-slate-700 dark:text-slate-200">
                      <li class="flex items-start gap-2">
                        <CheckIcon class="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                        <span>{{ $t('subscriptionPage.musicianFeatures.storage5GB') || '5GB de Armazenamento em Nuvem' }}</span>
                      </li>
                      <li class="flex items-start gap-2">
                        <CheckIcon class="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                        <span>{{ $t('subscriptionPage.musicianFeatures.exportProjects') || 'Exportação de faixas e mix' }}</span>
                      </li>
                    </ul>
                  </div>

                  <div>
                    <div class="text-[10px] font-bold uppercase tracking-wider text-amber-600 dark:text-amber-400 mb-1.5 flex items-center gap-1.5">
                      <SparklesIcon class="w-3 h-3 text-amber-500" />
                      <span>{{ $t('subscriptionPage.platformCreditsTitle') || 'Créditos de IA (Plataforma)' }}</span>
                    </div>
                    <ul class="space-y-1.5 text-[11px] text-slate-700 dark:text-slate-200">
                      <li class="flex items-start gap-2">
                        <CheckIcon class="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                        <span class="font-semibold">{{ $t('subscriptionPage.musicianFeatures.creditsMonth') || '20 Créditos / Mês para IA (Live & Studio)' }}</span>
                      </li>
                      <li class="flex items-start gap-2 text-slate-400 dark:text-slate-500">
                        <ClockIcon class="w-3.5 h-3.5 text-slate-400 dark:text-slate-500 shrink-0 mt-0.5" />
                        <span class="flex items-center gap-1.5 flex-wrap">
                          <span class="opacity-80">{{ $t('subscriptionPage.musicianFeatures.topUp') || 'Opção de recarga de créditos' }}</span>
                          <span class="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border border-slate-200/80 dark:border-slate-700/80">
                            {{ $t('subscriptionPage.comingSoon') || 'Em breve' }}
                          </span>
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <!-- 3. Backstage Tier (Pro & Teams) -->
            <div class="border border-purple-200 dark:border-purple-900/60 rounded-xl p-4 sm:p-5 flex flex-col justify-between bg-gradient-to-b from-purple-50/40 via-white to-white dark:from-purple-950/20 dark:via-slate-900 dark:to-slate-900 hover:border-purple-300 dark:hover:border-purple-700 transition-all text-left shadow-xs">
              <div class="space-y-3">
                <div class="flex items-center justify-between">
                  <div class="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-950/80 border border-purple-200/80 dark:border-purple-800/80 flex items-center justify-center text-purple-600 dark:text-purple-400 shadow-xs">
                    <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="m2 4 3 12h14l3-12-6 7-4-7-4 7-6-7zm3 16h14"/>
                    </svg>
                  </div>
                  <span class="px-2.5 py-0.5 rounded-md text-[10px] font-bold bg-purple-100 dark:bg-purple-950/80 text-purple-700 dark:text-purple-300 uppercase tracking-wider">{{ $t('subscriptionPage.proBadge') || 'Pro & Equipes' }}</span>
                </div>

                <div>
                  <h3 class="text-lg font-bold text-purple-600 dark:text-purple-400">
                    {{ $t('subscriptionPage.planNames.backstage') || 'The Backstage' }}
                  </h3>
                  <p class="text-[11px] text-slate-500 dark:text-slate-400">
                    {{ $t('subscriptionPage.planTaglines.backstage') || 'Poder Profissional & IA' }}
                  </p>
                </div>

                <div>
                  <div class="text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">
                    {{ displayBackstagePrice }}
                    <span class="text-xs font-normal text-slate-500 dark:text-slate-400">{{ perMonthText }}</span>
                  </div>
                  <div class="text-[11px] min-h-[16px]">
                    <span v-if="billingCycle === 'yearly'" class="text-emerald-600 dark:text-emerald-400 font-semibold">
                      {{ displayBackstageYearlyTotal }} {{ billedAnnuallyText }}
                    </span>
                    <span v-else class="text-slate-400 dark:text-slate-500">
                      {{ billedMonthlyText }}
                    </span>
                  </div>
                </div>

                <button v-if="isBackstage"
                        type="button" 
                        disabled 
                        class="w-full py-2.5 px-3 rounded-lg border border-purple-200 dark:border-purple-800 text-purple-600 dark:text-purple-400 text-xs font-bold bg-purple-50/60 dark:bg-purple-950/40 cursor-default text-center">
                  {{ $t('subscriptionPage.currentPlan') || 'Current Plan' }}
                </button>
                <button v-else-if="isAdmin"
                        type="button" 
                        disabled 
                        class="w-full py-2.5 px-3 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-400 dark:text-slate-500 text-xs font-bold bg-transparent cursor-default text-center">
                  {{ $t('subscriptionPage.included') || 'Included' }}
                </button>
                <button v-else-if="canSubscribe"
                        type="button" 
                        @click="handleSubscribe(billingCycle === 'monthly' ? STRIPE_PRICE_ID_BACKSTAGE_MONTHLY : STRIPE_PRICE_ID_BACKSTAGE_YEARLY)"
                        :disabled="isLoading"
                        class="w-full py-2.5 px-3 rounded-lg bg-purple-600 hover:bg-purple-700 active:scale-95 text-white text-xs font-bold shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed">
                  <svg v-if="loadingPriceId === (billingCycle === 'monthly' ? STRIPE_PRICE_ID_BACKSTAGE_MONTHLY : STRIPE_PRICE_ID_BACKSTAGE_YEARLY)" 
                       class="animate-spin h-3.5 w-3.5 text-purple-200" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
                  </svg>
                  <span>{{ isAmazon ? ($t('subscriptionPage.subscribeOnWeb') || 'Assinar no Site') : (isMusician ? ($t('subscriptionPage.upgradeTitle') || 'Upgrade') : ($t('subscriptionPage.subscribeBtn') || 'Subscribe')) }}</span>
                </button>
                <div v-else class="w-full py-2.5 px-3 rounded-lg bg-slate-100 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 text-xs font-medium text-center">
                  {{ isNative ? ($t('subscriptionPage.temporarilyUnavailable') || 'Indisponível no momento') : ($t('subscriptionPage.availableOnWeb') || 'Disponível no site') }}
                </div>

                <!-- Features Section -->
                <div class="space-y-3 pt-3 border-t border-purple-100 dark:border-purple-900/50 text-left">
                  <!-- Highlight: Everything in Musician -->
                  <div class="p-2.5 rounded-lg bg-purple-50/70 dark:bg-purple-950/40 border border-purple-200/60 dark:border-purple-800/60 flex items-center gap-2 text-xs font-bold text-purple-900 dark:text-purple-200">
                    <CheckIcon class="w-4 h-4 text-purple-600 dark:text-purple-400 shrink-0" />
                    <span>{{ $t('subscriptionPage.backstageFeatures.everythingInMusician') || 'Tudo do plano Musician' }}</span>
                  </div>

                  <div>
                    <div class="text-[10px] font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400 mb-1.5 flex items-center gap-1.5">
                      <span class="w-1.5 h-1.5 rounded-full bg-indigo-500"></span>
                      <span>Triad Live (Ilimitado)</span>
                    </div>
                    <ul class="space-y-1.5 text-[11px] text-slate-700 dark:text-slate-200">
                      <li class="flex items-start gap-2">
                        <CheckIcon class="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                        <span class="font-semibold">{{ $t('subscriptionPage.backstageFeatures.liveUnlimited') || 'Playlists e equipes ilimitadas' }}</span>
                      </li>
                      <li class="flex items-start gap-2">
                        <CheckIcon class="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                        <span>{{ $t('subscriptionPage.backstageFeatures.liveSongsLimit') || 'Até 30 músicas por playlist' }}</span>
                      </li>
                      <li class="flex items-start gap-2">
                        <CheckIcon class="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                        <span>{{ $t('subscriptionPage.backstageFeatures.liveAdvancedTeams') || 'Gestão avançada de equipes & sync tempo real' }}</span>
                      </li>
                    </ul>
                  </div>

                  <div>
                    <div class="text-[10px] font-bold uppercase tracking-wider text-purple-600 dark:text-purple-400 mb-1.5 flex items-center gap-1.5">
                      <span class="w-1.5 h-1.5 rounded-full bg-purple-500"></span>
                      <span>Triad Studio</span>
                    </div>
                    <ul class="space-y-1.5 text-[11px] text-slate-700 dark:text-slate-200">
                      <li class="flex items-start gap-2">
                        <CheckIcon class="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                        <span>{{ $t('subscriptionPage.backstageFeatures.storage20GB') || '20GB de Armazenamento em Nuvem' }}</span>
                      </li>
                      <li class="flex items-start gap-2">
                        <CheckIcon class="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                        <span>{{ $t('subscriptionPage.backstageFeatures.priorityProcessing') || 'Processamento IA Prioritário (Fast)' }}</span>
                      </li>
                    </ul>
                  </div>

                  <div>
                    <div class="text-[10px] font-bold uppercase tracking-wider text-amber-600 dark:text-amber-400 mb-1.5 flex items-center gap-1.5">
                      <SparklesIcon class="w-3 h-3 text-amber-500" />
                      <span>{{ $t('subscriptionPage.platformCreditsTitle') || 'Créditos de IA (Plataforma)' }}</span>
                    </div>
                    <ul class="space-y-1.5 text-[11px] text-slate-700 dark:text-slate-200">
                      <li class="flex items-start gap-2">
                        <CheckIcon class="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                        <span class="font-semibold">{{ $t('subscriptionPage.backstageFeatures.creditsMonth50') || '50 Créditos / Mês para IA (Live & Studio)' }}</span>
                      </li>
                      <li class="flex items-start gap-2">
                        <CheckIcon class="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                        <span>{{ $t('subscriptionPage.backstageFeatures.everythingInMusician') || 'Tudo do plano Musician' }}</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

          </div>

          <!-- Disclaimer Footer & Store Compliance Links -->
          <div class="text-center text-[11px] text-slate-400 dark:text-slate-500 pt-4 sm:pt-5 space-y-2">
            <div>
              {{ $t('subscriptionPage.pricingDisclaimer') || 'Prices in USD. Taxes may apply. Subscription auto-renews. Cancel anytime.' }}
            </div>

            <div class="flex flex-wrap items-center justify-center gap-2 sm:gap-3 text-[11px] text-slate-500 dark:text-slate-400">
              <!-- Android & iOS Only: Manage Subscription link -->
              <button 
                v-if="showModalManageButton"
                type="button" 
                @click="handleManageSubscriptions" 
                class="font-semibold text-indigo-600 dark:text-indigo-400 hover:underline cursor-pointer">
                {{ modalManageButtonText }}
              </button>
              <span v-if="showModalManageButton">•</span>

              <!-- Web Only: Cancel Subscription / Cancel Scheduled -->
              <template v-if="showModalCancelScheduledBadge">
                <span class="font-semibold text-amber-600 dark:text-amber-400">
                  {{ $t('subscriptionPage.cancelScheduled', 'Cancelamento agendado') }}
                </span>
                <span>•</span>
              </template>
              <template v-else-if="showModalCancelButton">
                <button 
                  type="button" 
                  @click="handleCancelSubscription" 
                  :disabled="isCanceling"
                  class="font-semibold text-red-600 dark:text-red-400 hover:underline cursor-pointer disabled:opacity-50">
                  {{ isCanceling ? 'Cancelando...' : ($t('subscriptionPage.cancelSubscription', 'Cancelar Assinatura')) }}
                </button>
                <span>•</span>
              </template>

              <!-- Android & iOS Only: Restore Purchases -->
              <button 
                v-if="isNative && hasKey" 
                type="button" 
                @click="handleRestorePurchases" 
                :disabled="isRestoring"
                class="font-semibold text-indigo-600 dark:text-indigo-400 hover:underline cursor-pointer disabled:opacity-50">
                {{ isRestoring ? 'Restaurando...' : ($t('subscriptionPage.restorePurchases', 'Restaurar Compras')) }}
              </button>
              <span v-if="isNative && hasKey">•</span>
              <a
                href="https://triadmusic.app/terms"
                target="_blank"
                rel="noopener noreferrer"
                @click.prevent="openExternalUrl('https://triadmusic.app/terms')"
                class="hover:underline hover:text-indigo-600 dark:hover:text-indigo-400">
                {{ $t('legal.termsOfService', 'Termos de Uso (EULA)') }}
              </a>
              <span>•</span>
              <a
                href="https://triadmusic.app/privacy"
                target="_blank"
                rel="noopener noreferrer"
                @click.prevent="openExternalUrl('https://triadmusic.app/privacy')"
                class="hover:underline hover:text-indigo-600 dark:hover:text-indigo-400">
                {{ $t('legal.privacyPolicy', 'Política de Privacidade') }}
              </a>
            </div>
          </div>

        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, defineExpose } from 'vue';
import { useI18n } from 'vue-i18n';
import { 
  XMarkIcon, 
  MusicalNoteIcon, 
  SparklesIcon, 
  CheckIcon 
} from '@heroicons/vue/24/solid';
import { ClockIcon } from '@heroicons/vue/24/outline';
import { appEvents, reloadProfile } from '@/store/app.events';
import userStore from '@/store/user.store';
import dialogStore from '@/store/dialog.store';
import toastStore from '@/store/toast.store';
import { 
  isNativePlatform, 
  isAmazonDevice, 
  checkIsAmazonDeviceAsync,
  getPublicWebBaseUrl, 
  openExternalUrl,
  watchExternalWindowClose
} from '@/utils/capacitor';
import { App } from '@capacitor/app';
import {
  subscriptionService,
  manageSubscriptions,
  initRevenueCat,
  isSubscriptionEnabled,
  hasRevenueCatKey,
  isIapSubscription,
  isStripeSubscription,
  getSubscriptionProvider,
  STRIPE_PRICE_ID_MUSICIAN_MONTHLY,
  STRIPE_PRICE_ID_MUSICIAN_YEARLY,
  STRIPE_PRICE_ID_BACKSTAGE_MONTHLY,
  STRIPE_PRICE_ID_BACKSTAGE_YEARLY
} from '@/service/subscription.service';

const { t } = useI18n();
const isOpen = ref(false);
const billingCycle = ref('monthly'); // 'monthly' | 'yearly'
const isLoading = ref(false);
const isRestoring = ref(false);
const isCanceling = ref(false);
const loadingPriceId = ref(null);
const isAmazonAsync = ref(false);
let appStateListener = null;

const isNative = computed(() => isNativePlatform());
const isAmazon = computed(() => isAmazonDevice() || isAmazonAsync.value);
const hasKey = computed(() => hasRevenueCatKey());
const canSubscribe = computed(() => !isNative.value || hasKey.value || isAmazon.value);

// Dynamic pricing from i18n language files
const currency = computed(() => t('subscriptionPage.prices.currency') || '$');
const musicianMonthlyPrice = computed(() => t('subscriptionPage.prices.musicianMonthly') || '8.99');
const musicianYearlyPrice = computed(() => t('subscriptionPage.prices.musicianYearly') || '89.90');
const musicianYearlyPerMonthPrice = computed(() => t('subscriptionPage.prices.musicianYearlyMonthlyEquivalent') || '7.49');

const backstageMonthlyPrice = computed(() => t('subscriptionPage.prices.backstageMonthly') || '15.99');
const backstageYearlyPrice = computed(() => t('subscriptionPage.prices.backstageYearly') || '159.99');
const backstageYearlyPerMonthPrice = computed(() => t('subscriptionPage.prices.backstageYearlyMonthlyEquivalent') || '13.33');

const perMonthText = computed(() => t('subscriptionPage.prices.perMonth') || '/mo');
const billedAnnuallyText = computed(() => t('subscriptionPage.prices.billedAnnuallyPrefix') || 'faturado anualmente');
const billedMonthlyText = computed(() => t('subscriptionPage.prices.billedMonthlyPrefix') || 'faturado mensalmente');

// Live StoreKit / RevenueCat offerings for dynamic pricing
const storeOfferings = ref(null);

const findPackage = (isBackstage, isYearly) => {
  if (!storeOfferings.value) return null;
  const currentPackages = storeOfferings.value.current?.availablePackages || [];
  const candidatePackages = [...currentPackages];
  if (storeOfferings.value.all) {
    Object.values(storeOfferings.value.all).forEach(off => {
      (off?.availablePackages || []).forEach(pkg => {
        if (!candidatePackages.some(cp => cp.identifier === pkg.identifier && cp.product?.identifier === pkg.product?.identifier)) {
          candidatePackages.push(pkg);
        }
      });
    });
  }

  return candidatePackages.find(p => {
    const prodId = (p.product?.identifier || '').toLowerCase();
    const pkgId = (p.identifier || '').toLowerCase();
    const offId = (p.offeringIdentifier || '').toLowerCase();

    const matchesTier = isBackstage
      ? (prodId.includes('backstage') || pkgId.includes('backstage') || offId.includes('backstage'))
      : (prodId.includes('musician') || pkgId.includes('musician') || offId.includes('musician'));

    if (!matchesTier) return false;

    const matchesCycle = isYearly
      ? (prodId.includes('yearly') || prodId.includes('annual') || pkgId.includes('yearly') || pkgId.includes('annual') || pkgId === '$annual' || pkgId === '$rc_annual')
      : (prodId.includes('monthly') || pkgId.includes('month') || pkgId === '$monthly' || pkgId === '$rc_monthly');

    return matchesCycle;
  });
};

const musicianMonthlyPackage = computed(() => findPackage(false, false));
const musicianYearlyPackage = computed(() => findPackage(false, true));
const backstageMonthlyPackage = computed(() => findPackage(true, false));
const backstageYearlyPackage = computed(() => findPackage(true, true));

const displayMusicianPrice = computed(() => {
  if (billingCycle.value === 'monthly') {
    return musicianMonthlyPackage.value?.product?.priceString || `${currency.value}${musicianMonthlyPrice.value}`;
  }
  if (musicianYearlyPackage.value?.product?.price) {
    const rawSymbol = musicianYearlyPackage.value.product.priceString?.replace(/[0-9.,\s]/g, '') || currency.value;
    const perMonth = (musicianYearlyPackage.value.product.price / 12).toFixed(2);
    return `${rawSymbol}${perMonth}`;
  }
  return `${currency.value}${musicianYearlyPerMonthPrice.value}`;
});

const displayMusicianYearlyTotal = computed(() => {
  return musicianYearlyPackage.value?.product?.priceString || `${currency.value}${musicianYearlyPrice.value}`;
});

const displayBackstagePrice = computed(() => {
  if (billingCycle.value === 'monthly') {
    return backstageMonthlyPackage.value?.product?.priceString || `${currency.value}${backstageMonthlyPrice.value}`;
  }
  if (backstageYearlyPackage.value?.product?.price) {
    const rawSymbol = backstageYearlyPackage.value.product.priceString?.replace(/[0-9.,\s]/g, '') || currency.value;
    const perMonth = (backstageYearlyPackage.value.product.price / 12).toFixed(2);
    return `${rawSymbol}${perMonth}`;
  }
  return `${currency.value}${backstageYearlyPerMonthPrice.value}`;
});

const displayBackstageYearlyTotal = computed(() => {
  return backstageYearlyPackage.value?.product?.priceString || `${currency.value}${backstageYearlyPrice.value}`;
});

const refreshAmazonStatus = async () => {
  try {
    const isAz = await checkIsAmazonDeviceAsync();
    if (isAz) {
      isAmazonAsync.value = true;
    }
  } catch (e) {
    // Safe fallback
  }
};

const currentTier = computed(() => {
  return userStore.state.userInfo?.membershipTier || 
         userStore.state.userInfo?.membership_tier || 
         'free_membership';
});

const isFree = computed(() => {
  return userStore.getters['isFree'];
});

const isSubscribed = computed(() => {
  return !isFree.value;
});

const isMusician = computed(() => {
  const t = currentTier.value;
  return t === 'musician_tier' || t === 'musician';
});

const isBackstage = computed(() => {
  const t = currentTier.value;
  return t === 'backstage_tier' || t === 'backstage';
});

const isAdmin = computed(() => {
  const t = currentTier.value;
  return t === 'admin_tier' || t === 'admin' || t === 'admin_membership';
});

const isCancelAtPeriodEnd = computed(() => {
  return Boolean(userStore.state.userInfo?.cancelAtPeriodEnd);
});

const modalManageButtonText = computed(() => {
  const p = getSubscriptionProvider();
  if (p === 'revenuecat_play_store') {
    return t('subscriptionPage.manageOnGooglePlay') || 'Gerenciar no Google Play';
  }
  if (p === 'revenuecat_app_store') {
    return t('subscriptionPage.manageOnAppStore') || 'Gerenciar na App Store';
  }
  return t('subscriptionPage.manageSubscription') || 'Gerenciar Assinatura';
});

// Android e iOS, OU Web se a assinatura for de loja mobile (IAP)
const showModalManageButton = computed(() => {
  if (isFree.value || isAdmin.value) return false;
  if (isNative.value) return true;
  return isIapSubscription();
});

// Web: Botão Cancel Subscription (APENAS Stripe via Web/Amazon, NUNCA IAP de lojas mobile)
const showModalCancelButton = computed(() => {
  if (isNative.value) return false;
  if (isFree.value || isAdmin.value) return false;
  if (isCancelAtPeriodEnd.value) return false;
  if (isIapSubscription()) return false;
  return isStripeSubscription();
});

// Selo de cancelamento agendado na Web
const showModalCancelScheduledBadge = computed(() => {
  return !isNative.value && !isFree.value && isCancelAtPeriodEnd.value;
});

const open = () => {
  if (!isSubscriptionEnabled()) return;
  isOpen.value = true;
  refreshAmazonStatus();
  if (isNative.value && !isAmazon.value) {
    initRevenueCat(userStore.state.userInfo?.id).then(async () => {
      try {
        const offerings = await subscriptionService.getOfferings();
        if (offerings) {
          storeOfferings.value = offerings;
          const candidateList = [];
          const allOffs = offerings.all || {};
          Object.entries(allOffs).forEach(([offKey, offVal]) => {
            (offVal?.availablePackages || []).forEach(pkg => {
              candidateList.push({
                offering: offKey,
                package: pkg.identifier,
                product: pkg.product?.identifier,
                price: pkg.product?.priceString,
                title: pkg.product?.title
              });
            });
          });
          console.log('[RevenueCat] Available offerings on open:', {
            currentOffering: offerings.current?.identifier,
            totalPackages: candidateList.length,
            packages: candidateList
          });
        }
      } catch (err) {
        console.warn('[RevenueCat] Could not prefetch offerings:', err);
      }
    });
  }
};

const close = () => {
  isOpen.value = false;
  loadingPriceId.value = null;
  isLoading.value = false;
};

const handleSubscribe = async (priceId) => {
  if (!priceId) return;

  isLoading.value = true;
  loadingPriceId.value = priceId;

  // On Web or Web PWA, pre-open a blank tab synchronously during user click to prevent popup blockers
  let popupWindow = null;
  if (!isNativePlatform()) {
    try {
      popupWindow = window.open('about:blank', '_blank');
    } catch (e) {
      console.warn('Could not pre-open blank tab:', e);
    }
  }

  try {
    const returnUrl = isNativePlatform()
      ? `${getPublicWebBaseUrl()}/profile`
      : window.location.href;

    const onStripeClosed = async () => {
      console.log('[SubscriptionModal] Stripe checkout window closed, reloading profile and closing modal...');
      try {
        await userStore.dispatch('info', true);
        reloadProfile();
        close();
      } catch (err) {
        console.warn('Error refreshing profile after Stripe checkout window closed:', err);
      }
    };

    const data = await subscriptionService.subscribe(priceId, returnUrl);
    if (data && (data.url || data.checkoutUrl)) {
      const checkoutUrl = data.url || data.checkoutUrl;
      if (isNativePlatform()) {
        // App Android or Apple (including Amazon Fire tablet): opens in the device's browser
        watchExternalWindowClose(null, onStripeClosed);
        await openExternalUrl(checkoutUrl);
      } else {
        // Web or Web PWA: opens in browser using _blank page
        if (popupWindow && !popupWindow.closed) {
          watchExternalWindowClose(popupWindow, onStripeClosed);
          popupWindow.location.href = checkoutUrl;
          popupWindow.focus();
        } else {
          const openedWin = window.open(checkoutUrl, '_blank', 'noopener,noreferrer');
          watchExternalWindowClose(openedWin, onStripeClosed);
        }
      }
    } else if (data && data.native) {
      if (popupWindow && !popupWindow.closed) {
        popupWindow.close();
      }
      await userStore.dispatch('updateBasicInfo');
      toastStore.dispatch('addMessage', {
        message: 'Assinatura confirmada com sucesso!',
        icon: 'success',
        ttl: 4000
      });
      close();
    } else {
      if (popupWindow && !popupWindow.closed) {
        popupWindow.close();
      }
    }
  } catch (error) {
    if (popupWindow && !popupWindow.closed) {
      popupWindow.close();
    }
    const isUserCancelled = error?.userCancelled || 
                            error?.code === '1' || 
                            error?.code === 1 || 
                            String(error?.message || '').toLowerCase().includes('cancel');
    if (isUserCancelled) {
      console.log('[SubscriptionModal] Purchase cancelled by user');
      return;
    }
    console.error('Failed to start subscription:', error);
    toastStore.dispatch('addMessage', {
      message: error.message || 'Falha ao processar assinatura',
      icon: 'error',
      ttl: 5000
    });
  } finally {
    isLoading.value = false;
    loadingPriceId.value = null;
  }
};

const handleRestorePurchases = async () => {
  isRestoring.value = true;
  try {
    const customerInfo = await subscriptionService.restorePurchases();
    await userStore.dispatch('updateBasicInfo');

    const activeEntitlements = customerInfo?.entitlements?.active || {};
    const hasActive = Object.keys(activeEntitlements).length > 0;
    const isCancelScheduled = Boolean(userStore.state.userInfo?.cancelAtPeriodEnd);

    if (hasActive) {
      if (isCancelScheduled) {
        toastStore.dispatch('addMessage', {
          message: t('subscriptionPage.restoreScheduledNotice') || 'Assinatura localizada. Seu acesso continua ativo até o término do ciclo, com o cancelamento mantido.',
          icon: 'info',
          ttl: 5000
        });
      } else {
        toastStore.dispatch('addMessage', {
          message: t('subscriptionPage.restoreSuccess') || 'Assinatura ativa restaurada com sucesso!',
          icon: 'success',
          ttl: 4000
        });
        close();
      }
    } else {
      toastStore.dispatch('addMessage', {
        message: t('subscriptionPage.restoreNotFound') || 'Nenhuma assinatura ativa encontrada para esta conta da loja.',
        icon: 'info',
        ttl: 4500
      });
    }
  } catch (error) {
    console.warn('Failed to restore purchases:', error);
    toastStore.dispatch('addMessage', {
      message: error?.message || t('subscriptionPage.restoreError') || 'Não foi possível restaurar as compras. Tente novamente.',
      icon: 'error',
      ttl: 4000
    });
  } finally {
    isRestoring.value = false;
  }
};

const handleManageSubscriptions = async () => {
  await manageSubscriptions();
};

const handleCancelSubscription = async () => {
  const confirmed = await dialogStore.dispatch('confirm', {
    title: t('subscriptionPage.confirmCancelTitle') || 'Cancelar Assinatura',
    message: t('subscriptionPage.confirmCancelMessage') || 'Tem certeza de que deseja cancelar sua assinatura? Você continuará tendo acesso a todos os benefícios até o final do período de faturamento.'
  });

  if (!confirmed) {
    return;
  }

  try {
    isCanceling.value = true;
    await subscriptionService.cancelSubscription({ immediately: false });

    // Atualização otimista e reativa imediata no store para sumir o botão e exibir o selo sem delay
    if (userStore.state.userInfo) {
      userStore.commit('INFO', {
        ...userStore.state.userInfo,
        cancelAtPeriodEnd: true
      });
    }

    toastStore.dispatch('addMessage', {
      message: t('subscriptionPage.cancelSuccess') || 'Assinatura cancelada com sucesso. O seu acesso permanecerá ativo até o final do período faturado.',
      icon: 'success',
      ttl: 5000
    });
    await userStore.dispatch('updateBasicInfo');
  } catch (error) {
    toastStore.dispatch('addMessage', {
      message: error.message || t('subscriptionPage.cancelError') || 'Erro ao cancelar assinatura. Tente novamente mais tarde.',
      icon: 'error',
      ttl: 5000
    });
  } finally {
    isCanceling.value = false;
  }
};

const handleKeyDown = (e) => {
  if (e.key === 'Escape' && isOpen.value) {
    close();
  }
};

const handleVisibilityChange = async () => {
  if (document.visibilityState === 'visible' && isOpen.value) {
    await userStore.dispatch('updateBasicInfo');
  }
};

onMounted(async () => {
  refreshAmazonStatus();
  appEvents.on('openSubscriptionModal', open);
  appEvents.on('closeSubscriptionModal', close);
  window.addEventListener('keydown', handleKeyDown);
  window.addEventListener('visibilitychange', handleVisibilityChange);

  if (isNativePlatform()) {
    try {
      appStateListener = await App.addListener('appStateChange', async (state) => {
        if (state.isActive && isOpen.value) {
          await userStore.dispatch('updateBasicInfo');
        }
      });
    } catch (e) {
      // Safe fallback if App listener is unavailable
    }
  }
});

onBeforeUnmount(() => {
  appEvents.off('openSubscriptionModal', open);
  appEvents.off('closeSubscriptionModal', close);
  window.removeEventListener('keydown', handleKeyDown);
  window.removeEventListener('visibilitychange', handleVisibilityChange);
  if (appStateListener && typeof appStateListener.remove === 'function') {
    appStateListener.remove();
    appStateListener = null;
  }
});

defineExpose({
  open,
  close
});
</script>

<style scoped>
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
  transform: scale(0.98);
}

.no-scrollbar::-webkit-scrollbar {
  display: none;
}
.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>
